import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useAuth, authorizedAxios } from "@moonscape/shared";

type DownloadTaxReportArgs = {
  taxYear: string;
  sampleMode?: boolean;
};

const taxModes: any[] = [
  {
    key: "tax-report",
    name: "Tax Report",
    detail: "Comprehensive tax report for the year",
    type: "PDF",
  },
  {
    key: "txns",
    name: "Transactions Report",
    detail: "Detailed records of every transaction",
    type: "PDF",
  },
  {
    key: "all",
    name: "All",
    detail: "Download all reports in your account",
    type: "ZIP",
  },
];

export const useDownloadTaxReport = ({
  onSuccess,
  onError,
}: {
  onSuccess?: () => void;
  onError?: (err: unknown) => void;
}) => {
  const { getToken } = useAuth();
  const [selectedMode, setSelectedMode] = useState<string | null>(
    taxModes[0]?.key
  );

  const mutation = useMutation({
    mutationFn: async ({ taxYear, sampleMode }: DownloadTaxReportArgs) => {
      const token = await getToken();
      const response = await authorizedAxios(token).get(
        `/export?year=${taxYear}&mode=${selectedMode}&sample=${sampleMode}`,
        {
          responseType: "blob", // if it's a file
        }
      );
      return response;
    },
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (err) => {
      onError?.(err);
      console.error("[useDownloadTaxReport] Error", err);
    },
  });

  return {
    downloadTaxReport: mutation.mutate, // call with { taxYear, downloadType }
    downloadTaxReportAsync: mutation.mutateAsync,
    loading: mutation.isPending,
    error: mutation.error,
    data: mutation.data,
    taxModes,
    sampleMode: mutation.variables?.sampleMode,
    selectedMode,
    setSelectedMode,
  };
};
